/* eslint-disable no-prototype-builtins */
import type { Entity } from '@getaccept/lib-shared-new/src/types/entity';
import type { AnalyticsEvent, AnalyticsIntegrationEvent } from './types/analytics';
import { AnalyticsCompanyTrait, AnalyticsUserTrait } from './types/analytics';
import Pendo from './pendo';
import { convertDateStringToISOString, getGlobalHandle } from './helpers';
import Segment from './segment';
import type { Session } from './types/Session';
import type { User } from './users/types/user';

export default class Analytics {
  static initializeCoreApp(session: Session, user: User, entity: Entity): void {
    const isSubEntity = session.is_sub_entity;

    const userTraits: Partial<Record<AnalyticsUserTrait, any>> = {
      [AnalyticsUserTrait.UserHash]: session.user_hash,
      [AnalyticsUserTrait.Email]: session.email,
      [AnalyticsUserTrait.FirstName]: session.first_name,
      [AnalyticsUserTrait.LastName]: session.last_name,
      [AnalyticsUserTrait.Mobile]: session.mobile,
      [AnalyticsUserTrait.Title]: user?.title,
      [AnalyticsUserTrait.Plan]: session.plan,
      [AnalyticsUserTrait.Avatar]: session.thumb_url,
      [AnalyticsUserTrait.Role]: session.user_role,
      [AnalyticsUserTrait.Language]: session.language,
      [AnalyticsUserTrait.Currency]: session.currency,
      [AnalyticsUserTrait.RegistrationSource]: session.registration_source,
      [AnalyticsUserTrait.RegistrationCampaign]: session.registration_campaign,
      [AnalyticsUserTrait.RegistrationDevice]: session.registration_device,
      [AnalyticsUserTrait.AchievementsLevel]: session.achievements_level,
      [AnalyticsUserTrait.AppMode]: session.app_mode,
      [AnalyticsUserTrait.CreatedAt]: convertDateStringToISOString(session.user_created_at),
      [AnalyticsUserTrait.CompanyId]: session.entity_hash,
      [AnalyticsUserTrait.CompanyName]: session.entity_name,
      [AnalyticsUserTrait.CompanyPlan]: session.plan,
      [AnalyticsUserTrait.CompanyCreatedAt]: convertDateStringToISOString(
        session.entity_created_at
      ),
      [AnalyticsUserTrait.IsSubEntity]: isSubEntity ? true : undefined,
      [AnalyticsUserTrait.ParentEntityId]: isSubEntity ? entity.parentEntityId : undefined,
      [AnalyticsUserTrait.AssetExtId]: isSubEntity ? session.entity_hash : undefined,
    };

    const companyTraits: Partial<Record<AnalyticsCompanyTrait, any>> = {
      [AnalyticsCompanyTrait.Name]: session.entity_name,
      [AnalyticsCompanyTrait.Plan]: session.plan,
      [AnalyticsCompanyTrait.CreatedAt]: convertDateStringToISOString(session.entity_created_at),
      [AnalyticsCompanyTrait.IsSubEntity]: isSubEntity ? true : undefined,
      [AnalyticsCompanyTrait.ParentEntityId]: isSubEntity ? entity.parentEntityId : undefined,
    };

    Segment.identify(session.user_hash, userTraits, {
      integrations: {
        Intercom: {
          user_hash: session.iio_hash,
          language_override: session.language,
        },
      },
    });

    Segment.group(session.entity_hash, companyTraits);

    if (!getGlobalHandle().analytics.hasOwnProperty('initialized') && !!getGlobalHandle().pendo) {
      Pendo.initialize({
        visitor: { ...userTraits, id: session.user_hash },
        account: { ...companyTraits, id: session.entity_hash },
      });
    }
  }
  static identify(
    userId: string | undefined,
    userTraits: Partial<Record<AnalyticsUserTrait, any>>
  ): void {
    Segment.identify(userId, userTraits);

    if (!getGlobalHandle().analytics.hasOwnProperty('initialized') && !!getGlobalHandle().pendo) {
      const pendoUserId = userId || Pendo.getVisitorId();

      Pendo.identify({
        visitor: {
          [AnalyticsUserTrait.ID]: pendoUserId,
          ...userTraits,
        },
      });
    }
  }
  static track(
    event: AnalyticsEvent | AnalyticsIntegrationEvent,
    properties?: Record<string, any>
  ): void {
    const segmentInitialized = getGlobalHandle().analytics.hasOwnProperty('initialized');
    const entityId = segmentInitialized ? getGlobalHandle().analytics._group.id() : undefined;
    const isSubEntity = segmentInitialized
      ? getGlobalHandle().analytics._group.traits()[AnalyticsCompanyTrait.IsSubEntity]
      : undefined;
    const parentEntityId = segmentInitialized
      ? getGlobalHandle().analytics._group.traits()[AnalyticsCompanyTrait.ParentEntityId]
      : undefined;

    Segment.track(event, {
      ...properties,
      groupId: entityId,
      [AnalyticsCompanyTrait.ParentEntityId]: isSubEntity ? parentEntityId : undefined,
      [AnalyticsCompanyTrait.AssetExtId]: isSubEntity ? entityId : undefined,
    });

    if (!segmentInitialized && !!getGlobalHandle().pendo) {
      Pendo.track(event, properties);
    }
  }
}
